/* The H2 review has no list of its own in the payload: the items that finish it are the plan
   review actions the cockpit already carries, and the `cockpitPendingActions` layer is where they
   are scoped, sorted, joined to their owner's name and given their dialogs. This section borrows
   that action layer — not the pending-actions tile, which would put a second tile with its own
   tabs and counters inside the card. */
import { useId, useMemo, useState } from 'react'

import { Translation } from 'cng-common/features/translations'
import { ATTENTION_STATUS_BITS } from 'partner360/entities/attentionStatuses'

import { FinancialCockpitPendingActionOwner } from '../../../../api/types'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { usePendingActionsGroups } from '../../../cockpitPendingActions/hooks/usePendingActionsGroups'
import type { IPendingActionWithOffering } from '../../../cockpitPendingActions/types'
import { useAttentionDetailOffering } from '../../hooks/useAttentionDetailOffering'
import { useAttentionRemindersHiddenByItems } from '../../hooks/useAttentionRemindersHiddenByItems'
import { AttentionStatusActionRows } from '../attentionStatusActionRow'
import { AttentionStatusActions } from '../attentionStatusActions'
import {
  ATTENTION_OWNER_ORDER,
  AttentionStatusOwnerTabs,
  getAttentionOwnerPanelId,
  getAttentionOwnerTabId,
} from '../attentionStatusOwnerTabs'

import styles from './AttentionStatusH2Review.module.scss'

const H2_REVIEW_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.h2Review
const DETAIL_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.detail

/**
 * The review is stated for one offering at a time, and `usePendingActionsGroups` narrows to one
 * owner rather than to one offering — so each call is cut down to the owner it was asked for and
 * to the offering the status was raised for.
 */
const scopeActions = (
  actions: IPendingActionWithOffering[],
  owner: FinancialCockpitPendingActionOwner,
  offering: string | null
) => actions.filter((action) => action.ActionOwner === owner && action.Offering === offering)

/**
 * The detail of the `Offering X is not reviewed for H2` status: what is still open in that
 * review, split by whose it is — the reader's own items carrying the call that finishes them, the
 * other side's carrying the reminder to whoever holds them.
 *
 * The owners are tabs, the same strip the pending-actions card switches its list with: the reader
 * answers one side at a time, and stacked blocks put the second owner's rows below a list that is
 * already long enough to scroll.
 *
 * The split is not a role check. The payload marks an action `Mine` for whoever is reading it, so
 * the same list reads correctly for the PBM and for the Manager without this section ever asking
 * which of them opened the card.
 *
 * `usePendingActionsGroups` is asked three times, once per owner: given no owner tab it falls
 * back to the first owner it finds and returns that owner alone, so one call could never hold all
 * three groups.
 */
export const AttentionStatusH2Review = () => {
  const tabsId = useId()
  const [pickedOwner, setPickedOwner] = useState<FinancialCockpitPendingActionOwner | null>(null)
  const activeOffering = useAttentionDetailOffering()

  const { actions: mineInScope } = usePendingActionsGroups(
    FinancialCockpitPendingActionOwner.Mine,
    null,
    false
  )
  const { actions: pbmInScope } = usePendingActionsGroups(
    FinancialCockpitPendingActionOwner.PBM,
    null,
    false
  )
  const { actions: managerInScope } = usePendingActionsGroups(
    FinancialCockpitPendingActionOwner.SalesManager,
    null,
    false
  )

  const mineActions = useMemo(
    () => scopeActions(mineInScope, FinancialCockpitPendingActionOwner.Mine, activeOffering),
    [mineInScope, activeOffering]
  )
  const pbmActions = useMemo(
    () => scopeActions(pbmInScope, FinancialCockpitPendingActionOwner.PBM, activeOffering),
    [pbmInScope, activeOffering]
  )
  const managerActions = useMemo(
    () =>
      scopeActions(managerInScope, FinancialCockpitPendingActionOwner.SalesManager, activeOffering),
    [managerInScope, activeOffering]
  )

  const remindersHiddenByItems = useAttentionRemindersHiddenByItems(
    ATTENTION_STATUS_BITS.h2Review,
    {
      mine: mineActions.length,
      pbm: pbmActions.length,
      manager: managerActions.length,
    }
  )

  const ownerGroups = useMemo(
    () =>
      [mineActions, pbmActions, managerActions]
        .map((actions, index) => ({
          owner: ATTENTION_OWNER_ORDER[index],
          actions,
          count: actions.length,
        }))
        .filter(({ count }) => count > 0),
    [mineActions, pbmActions, managerActions]
  )

  /* The tab the reader picked, as long as the list still carries it: switching the offering, or a
     refresh that clears an owner's last item, would otherwise leave the card on an empty tab. */
  const activeOwner =
    ownerGroups.find(({ owner }) => owner === pickedOwner)?.owner ?? ownerGroups[0]?.owner ?? null
  const activeGroup = ownerGroups.find(({ owner }) => owner === activeOwner)

  /* The review is held per offering, and the cockpit's filters decide which offerings a partner
     shows: with none of them left there is nothing to review, and an empty card would read as a
     section that failed to load. */
  if (!activeOffering) {
    return (
      <div className={styles.root}>
        <p className={styles.empty}>
          <Translation span text={DETAIL_KEYS.noOffering} />
        </p>
      </div>
    )
  }

  return (
    <div className={styles.root}>
      {ownerGroups.length === 0 ? (
        <p className={styles.empty}>
          <Translation span text={H2_REVIEW_KEYS.noItems} />
        </p>
      ) : (
        <div className={styles.groups}>
          <AttentionStatusOwnerTabs
            tabs={ownerGroups}
            activeOwner={activeOwner}
            onSelect={setPickedOwner}
            idPrefix={tabsId}
          />

          {activeGroup && (
            <div
              key={activeGroup.owner}
              role="tabpanel"
              id={getAttentionOwnerPanelId(tabsId, activeGroup.owner)}
              aria-labelledby={getAttentionOwnerTabId(tabsId, activeGroup.owner)}
              className={styles.rows}
            >
              {/* Every group is drawn by the same row: the reader's own items carry the call that
              finishes them, dialogs and blocked locks included, and the other side's carry the
              reminder to whoever holds them. Which of the two a row gets is the row's, read off
              the action's owner, so this section never asks who is reading it. */}
              <AttentionStatusActionRows offering={activeOffering} actions={activeGroup.actions} />
            </div>
          )}
        </div>
      )}

      {/* The rows above carry the review itself — one row per item, as the sheet draws it — so
      the bar drops it. The reminder stays, in the card's corner with every other act. */}
      <AttentionStatusActions
        bitValue={ATTENTION_STATUS_BITS.h2Review}
        className={styles.actions}
        hidden={['reviewH2', ...remindersHiddenByItems]}
      />
    </div>
  )
}

AttentionStatusH2Review.displayName = 'AttentionStatusH2Review'
